import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import type { AppState, Dispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import { unaccentAndLower } from '@tmapy/beetlejuice-utils';

import { MultiSelectProps, Select } from '@tmapy/style-guide';
import {
  BtnBar,
  Form,
  FormItem,
  FormRow,
  Input,
  MultiSelect,
  CtaBtn,
  SecondaryBtn,
  StickyFooter,
} from '@tmapy/style-guide';

import type { OmittedFormItemProps } from '../../types';
import type { InputProps } from '../../hooks/useInput';
import { useAuth, useInput, useMultiSelect } from '../../hooks';
import { setOrderListFilter } from '../../redux/filter/actions';
import { ORDER_FROM_PRIMARY_DB, ORDER_IS_PUBLIC } from '../../constants';

import { OrderSelectionTypesEnum } from '../../openapi/types/OrderSelectionTypesEnum';
import { useFormatsListResource } from '../../openapi/operations/formatsList';
import { OrdersListQuery, OrdersListResponse } from '../../openapi/operations/ordersList';
import { OriginEnum } from '../../openapi/types/OriginEnum';

import { setMenuRouteFilterId } from '../../redux/menu/actions';
import { compareStringArrays } from '../utils/compareStringArrays';
import {
  ORDER_DMVS_SOURCE_ITEMS,
  ORDER_STATE,
  STATUS_ZADATELE_FILTER,
  usableItemsForFormType,
} from '../constants';
import {
  orderStateMessages,
  orderFilterMessages,
  typesMessages,
  statusZadateleFilterMessages,
  dmvsSourceFilterMessages,
  fromPrimaryDBMessages,
  isPublicMessages,
  originMessages,
} from './orderMessages';

type OrderFiltersProps = {
  filters: OrdersListQuery;
  dispatch: Dispatch;
};

export const OrderFilter: React.FC<OrderFiltersProps> = ({ filters, dispatch }) => {
  const formatMessage = useMessage();
  const { customerType } = useSelector((state: AppState) => state.app.config);
  const usableItems = usableItemsForFormType[customerType];

  const auth = useAuth();
  const acl = useMemo(
    () => ({
      ordersActionHiddenCreator: auth.hasPermission('Orders', 'action.hidden_creator', null),
    }),
    [auth],
  );

  const number = useInput<InputProps>({
    name: 'id',
    initialValue: filters.id as any,
    label: formatMessage(orderFilterMessages.number),
  });

  const stateItems = useSelectMessages(orderStateMessages, ORDER_STATE);

  const prepareStateItems = useCallback(
    (val: string) => {
      return stateItems.filter(
        (state) => unaccentAndLower(state.label).indexOf(unaccentAndLower(val)) >= 0,
      );
    },
    [stateItems],
  );

  const stateInitialValues = useMemo(() => {
    return filters.state__in
      ? stateItems.filter((state) => filters.state__in!.includes(state.value))
      : [];
  }, [stateItems, filters.state__in]);

  const state__in = useMultiSelect<
    MultiSelectProps<OrdersListResponse> | OmittedFormItemProps,
    (typeof stateItems)[0]
  >({
    name: 'state__in',
    label: formatMessage(orderFilterMessages.state),
    initialValue: stateInitialValues,
    prepareItems: prepareStateItems,
    renderValue: (state: (typeof stateItems)[0]) => state.label,
    renderItem: (state: (typeof stateItems)[0]) => state.label,
  });

  const typeItems = useSelectMessages(typesMessages, OrderSelectionTypesEnum);
  const selection_type = useInput({
    name: 'selection_type',
    initialValue: filters.selection_type,
    items: typeItems,
    label: formatMessage(orderFilterMessages.type),
  });

  const fromPrimaryDBItems = useSelectMessages(fromPrimaryDBMessages, ORDER_FROM_PRIMARY_DB);
  const from_primary_db = useInput({
    name: 'from_primary_db',
    initialValue: filters.from_primary_db as any,
    items: fromPrimaryDBItems,
    label: formatMessage(orderFilterMessages.fromPrimaryDB),
  });

  const isPublicItems = useSelectMessages(isPublicMessages, ORDER_IS_PUBLIC);
  const is_public = useInput({
    name: 'is_public',
    initialValue: filters.is_public as any,
    items: isPublicItems,
    label: formatMessage(orderFilterMessages.isPublic),
  });

  const originItems = useSelectMessages(originMessages, OriginEnum);
  const origin = useInput({
    name: 'origin',
    initialValue: filters.origin,
    items: originItems,
    label: formatMessage(orderFilterMessages.origin),
  });

  const dmvs_sourceItems = useSelectMessages(dmvsSourceFilterMessages, ORDER_DMVS_SOURCE_ITEMS);
  const dmvs_sourceInitValue = useMemo(() => {
    // DTM
    if (filters.dmvs_source__isnull) return 'DTM';
    // DMVS
    if (filters.dmvs_source__in?.length) {
      for (const [key, value] of Object.entries(ORDER_DMVS_SOURCE_ITEMS)) {
        if (compareStringArrays(filters.dmvs_source__in, value.split(','))) {
          return key;
        }
      }
    }

    return undefined;
  }, [filters.dmvs_source__isnull, filters.dmvs_source__in]);

  const dmvs_source = useInput({
    name: 'dmvs_source',
    initialValue: dmvs_sourceInitValue,
    items: dmvs_sourceItems,
    label: formatMessage(orderFilterMessages.dmvs_source),
  });

  const status_zadateleItems = useSelectMessages(
    statusZadateleFilterMessages,
    STATUS_ZADATELE_FILTER,
  );
  const status_zadatele = useInput({
    name: 'status_zadatele',
    initialValue: filters.status_zadatele,
    items: status_zadateleItems,
    label: formatMessage(orderFilterMessages.status_zadatele),
  });

  const creatorFullName = useInput<InputProps>({
    name: 'originator__icontains',
    initialValue: filters.originator__icontains,
    label: formatMessage(orderFilterMessages.creator),
  });

  const publishset_name__icontains = useInput<InputProps>({
    name: 'publishset_name__icontains',
    initialValue: filters.publishset_name__icontains,
    label: formatMessage(orderFilterMessages.publishset),
  });

  const dmvs_id_podani__icontains = useInput<InputProps>({
    name: 'dmvs_id_podani__icontains',
    initialValue: filters.dmvs_id_podani__icontains,
    label: formatMessage(orderFilterMessages.idPodani),
  });

  const formatsList = useFormatsListResource({}, true);
  const prepareFormatItems = useCallback(
    async (val: string) => {
      if (!formatsList.data || !formatsList.data.results) return null;
      return formatsList.data.results
        .filter((format) => unaccentAndLower(format.name).indexOf(unaccentAndLower(val)) >= 0)
        .map((format) => format.name);
    },
    [formatsList],
  );

  const formats__in = useMultiSelect<
    MultiSelectProps<OrdersListResponse> | OmittedFormItemProps,
    string
  >({
    name: 'formats__in',
    label: formatMessage(orderFilterMessages.formats),
    initialValue: filters.formats__in,
    prepareItems: prepareFormatItems,
    renderValue: (item: string) => item,
    renderItem: (item: string) => item,
  });

  const [createdGte, setCreateGt] = useState(filters.created__gte);
  const created__gte = {
    name: 'created__gte',
    clear: () => {
      setCreateGt('');
    },
  };
  const handleCreateGtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateGt(eventData.value);
  };

  const [createdLt, setCreateLt] = useState(filters.created__lt);
  const created__lt = {
    name: 'created__lt',
    clear: () => {
      setCreateLt('');
    },
  };
  const handleCreateLtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateLt(eventData.value);
  };

  const [publishedGte, setPublishedGt] = useState(filters.published__gte);
  const published__gte = {
    name: 'published__gte',
    clear: () => {
      setPublishedGt('');
    },
  };
  const handleFinishGtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setPublishedGt(eventData.value);
  };

  const [publishedLt, setPublishedLt] = useState(filters.published__lt);
  const published__lt = {
    name: 'published__lt',
    clear: () => {
      setPublishedLt('');
    },
  };
  const handleFinishLtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setPublishedLt(eventData.value);
  };

  const filterClearScope = [
    state__in,
    number,
    selection_type,
    dmvs_source,
    status_zadatele,
    creatorFullName,
    publishset_name__icontains,
    dmvs_id_podani__icontains,
    created__gte,
    created__lt,
    published__gte,
    published__lt,
    formats__in,
    from_primary_db,
    is_public,
    origin,
  ];

  function handleClearBtnClick() {
    filterClearScope.forEach((formItem) => formItem?.clear?.());
    dispatch(setOrderListFilter({}));

    // reset filters - reset/set menu item
    dispatch(setMenuRouteFilterId('vydejOrderList'));
  }

  function handleSubmit() {
    const dmvs_source__in =
      (ORDER_DMVS_SOURCE_ITEMS[dmvs_source.value]?.split(
        ',',
      ) as OrdersListQuery['dmvs_source__in']) || undefined;

    dispatch(
      setOrderListFilter({
        state__in: state__in.values.map((state) => state.value),
        [number.name!]: number.value,
        selection_type: selection_type.value,
        dmvs_source__in: dmvs_source.value === 'DTM' ? undefined : dmvs_source__in,
        dmvs_source__isnull: dmvs_source.value === 'DTM' ? true : undefined,
        status_zadatele: status_zadatele.value,
        [creatorFullName.name!]: creatorFullName.value,
        [publishset_name__icontains.name!]: publishset_name__icontains.value,
        dmvs_id_podani__icontains: dmvs_id_podani__icontains.value,
        created__gte: createdGte,
        created__lt: createdLt,
        published__gte: publishedGte,
        published__lt: publishedLt,
        formats__in: formats__in.values as any, // ENUM -> formats__in[]
        from_primary_db: from_primary_db.value as any, // ENUM -> boolean
        is_public: is_public.value as any, // ENUM -> boolean
        origin: origin.value,
      }),
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        <FormRow>
          {usableItems?.includes('state') && (
            <FormItem {...state__in}>
              <MultiSelect {...state__in} />
            </FormItem>
          )}
          <FormItem {...number}>
            <Input {...number} />
          </FormItem>
        </FormRow>
        {usableItems?.includes('selection_type') && (
          <FormRow>
            <FormItem {...selection_type}>
              <Select hasEmptyItem {...selection_type} />
            </FormItem>
          </FormRow>
        )}
        {usableItems?.includes('from_primary_db') && (
          <FormRow>
            <FormItem {...from_primary_db}>
              <Select hasEmptyItem {...from_primary_db} />
            </FormItem>
          </FormRow>
        )}
        {usableItems?.includes('is_public') && (
          <FormRow>
            <FormItem {...is_public}>
              <Select hasEmptyItem {...is_public} />
            </FormItem>
          </FormRow>
        )}
        {usableItems?.includes('dmvs_id_podani') && (
          <FormRow>
            <FormItem {...dmvs_id_podani__icontains}>
              <Input {...dmvs_id_podani__icontains} />
            </FormItem>
          </FormRow>
        )}
        {usableItems?.includes('dmvs_source') && (
          <FormRow>
            <FormItem {...dmvs_source}>
              <Select hasEmptyItem {...dmvs_source} />
            </FormItem>
          </FormRow>
        )}
        {usableItems?.includes('status_zadatele') && (
          <FormRow>
            <FormItem {...status_zadatele}>
              <Select hasEmptyItem {...status_zadatele} />
            </FormItem>
          </FormRow>
        )}
        {usableItems?.includes('origin') && !acl.ordersActionHiddenCreator && (
          <FormRow>
            <FormItem {...origin}>
              <Select hasEmptyItem {...origin} />
            </FormItem>
          </FormRow>
        )}
        {usableItems?.includes('creator') && !acl.ordersActionHiddenCreator && (
          <FormRow>
            <FormItem {...creatorFullName}>
              <Input {...creatorFullName} />
            </FormItem>
          </FormRow>
        )}
        {usableItems?.includes('publishsets') && (
          <>
            <FormRow>
              <FormItem {...publishset_name__icontains}>
                <Input {...publishset_name__icontains} />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem {...formats__in}>
                <MultiSelect {...formats__in} />
              </FormItem>
            </FormRow>
          </>
        )}
        {usableItems?.includes('created') && (
          <FormRow>
            <FormItem label={formatMessage(orderFilterMessages.createdGte)}>
              <Input
                type='date'
                name='created__gte'
                value={createdGte}
                onChange={handleCreateGtChange}
              />
            </FormItem>
            <FormItem label={formatMessage(orderFilterMessages.createdLt)}>
              <Input
                type='date'
                name='created__lt'
                value={createdLt}
                onChange={handleCreateLtChange}
              />
            </FormItem>
          </FormRow>
        )}
        {usableItems?.includes('published') && (
          <FormRow>
            <FormItem label={formatMessage(orderFilterMessages.publishedGte)}>
              <Input
                type='date'
                name='published__gte'
                value={publishedGte}
                onChange={handleFinishGtChange}
              />
            </FormItem>
            <FormItem label={formatMessage(orderFilterMessages.publishedLt)}>
              <Input
                type='date'
                name='published__lt'
                value={publishedLt}
                onChange={handleFinishLtChange}
              />
            </FormItem>
          </FormRow>
        )}
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...orderFilterMessages.submitBtn} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...orderFilterMessages.clearBtn} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
